import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import {
  alertConfigFromForm,
  AlertConfigFormGroup,
  createAlertConfigFormGroup,
  patchAlertConfigForm,
  setAlertConfigRetryScheduleActive,
} from '../../configuration/alert-config-form.builder';
import { AlertConfigFormFieldsComponent } from '../../configuration/alert-config-form-fields/alert-config-form-fields.component';
import { ConfigurationService } from '../../configuration/configuration.service';
import { DEFAULT_ALERT_CONFIG, resolveAlertConfig } from '../../configuration/configuration.defaults';
import { NotifyChannel } from '../../configuration/notify-channel.util';
import { AlertConfig } from '../user.service';
import { patientPhoneToE164 } from '../../notification/standalone-patient-contact';
import { MaterialModule } from '../../shared/material/material.module';
import { LocationProvider } from '../patient.model';
import {
  AlertCampaignService,
  AlertCampaignRosterLine,
} from './alert-campaign.service';
import {
  autoDetectColumnMapping,
  ColumnMapping,
  composeMappedRows,
} from './roster-column-map.util';
import {
  CAMPAIGN_CYCLE_UI_SLOTS,
  cycleSlotsForCampaignUi,
} from '../../notification/model/alert-cycle.util';
import { parseRosterFile, ParsedRosterFile } from './roster-file.parser';

function cycleSlotGroupValidator(required: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const date = (control.get('date')?.value ?? '').trim();
    const time = (control.get('time')?.value ?? '').trim();
    if (required) {
      return date && time ? null : { incomplete: true };
    }
    if ((date && !time) || (!date && time)) {
      return { incomplete: true };
    }
    return null;
  };
}

type PreviewRow = AlertCampaignRosterLine & {
  rowIndex: number;
  status: 'ready' | 'invalidPhone' | 'missingSubject' | 'duplicate' | 'skipped';
};

const STEP_TITLES = ['Upload', 'Map', 'Preview', 'Settings', 'Confirm'] as const;

@Component({
  selector: 'app-alert-roster-upload-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatDialogModule,
    AlertConfigFormFieldsComponent,
  ],
  templateUrl: './alert-roster-upload-wizard.component.html',
  styleUrl: './alert-roster-upload-wizard.component.scss',
})
export class AlertRosterUploadWizardComponent implements OnDestroy {
  #dialogRef = inject(MatDialogRef<AlertRosterUploadWizardComponent>);
  #destroy$ = new Subject<void>();
  #snackBar = inject(MatSnackBar);
  #router = inject(Router);
  #campaignService = inject(AlertCampaignService);
  #configurationService = inject(ConfigurationService);
  readonly stepTitles = STEP_TITLES;
  step = signal(0);
  starting = signal(false);

  parsedFile = signal<ParsedRosterFile | null>(null);
  columnMapping = signal<ColumnMapping>({ subjectColumns: [], phoneColumns: [] });
  previewRows = signal<PreviewRow[]>([]);
  subjectLabels = signal({
    subjectLabel: DEFAULT_ALERT_CONFIG.subjectLabel,
    subjectLabelPlural: DEFAULT_ALERT_CONFIG.subjectLabelPlural,
  });

  locations = signal<LocationProvider[]>([]);
  alertConfigForm: AlertConfigFormGroup = createAlertConfigFormGroup();

  settingsForm = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl('', { nonNullable: true }),
    locationId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    customScheduleEnabled: new FormControl(false, { nonNullable: true }),
    customSlots: new FormArray<
      FormGroup<{
        date: FormControl<string>;
        time: FormControl<string>;
      }>
    >([]),
  });

  constructor() {
    this.#ensureCycleSlotRows();
    this.#applyCustomScheduleMode(false);

    this.alertConfigForm.valueChanges.pipe(takeUntil(this.#destroy$)).subscribe(() => {
      if (!this.settingsForm.controls.customScheduleEnabled.value) {
        this.#syncComputedCycleSlots();
      }
    });

    this.settingsForm.controls.customScheduleEnabled.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe((custom) => this.#applyCustomScheduleMode(custom));

    this.#configurationService
      .getConfiguration()
      .pipe(take(1))
      .subscribe({
        next: ({ alertConfig }) => {
          const resolved = resolveAlertConfig(alertConfig ?? DEFAULT_ALERT_CONFIG);
          patchAlertConfigForm(this.alertConfigForm, resolved);
          this.subjectLabels.set({
            subjectLabel: resolved.subjectLabel,
            subjectLabelPlural: resolved.subjectLabelPlural,
          });
          this.#syncComputedCycleSlots();
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  setLocations(locations: LocationProvider[]): void {
    this.locations.set(locations);
    if (locations.length === 1) {
      this.settingsForm.controls.locationId.setValue(locations[0].id);
    }
  }

  stepLabel(): string {
    return `Step ${this.step() + 1} of ${STEP_TITLES.length} ${STEP_TITLES[this.step()]}`;
  }

  onCancel(): void {
    this.#dialogRef.close();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    try {
      const parsed = await parseRosterFile(file);
      this.parsedFile.set(parsed);
      const mapping = autoDetectColumnMapping(parsed.headers, parsed.rows);
      this.columnMapping.set(mapping);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not read file.';
      this.#snackBar.open(message, 'Okay', { duration: 6000 });
      input.value = '';
    }
  }

  headers(): string[] {
    return this.parsedFile()?.headers ?? [];
  }

  addSubjectColumn(): void {
    const headers = this.headers();
    if (!headers.length) {
      return;
    }
    const mapping = this.columnMapping();
    const next = headers.find((h) => !mapping.subjectColumns.includes(h));
    if (next) {
      this.columnMapping.set({ ...mapping, subjectColumns: [...mapping.subjectColumns, next] });
    }
  }

  addPhoneColumn(): void {
    const headers = this.headers();
    if (!headers.length) {
      return;
    }
    const mapping = this.columnMapping();
    const next = headers.find((h) => !mapping.phoneColumns.includes(h));
    if (next) {
      this.columnMapping.set({ ...mapping, phoneColumns: [...mapping.phoneColumns, next] });
    }
  }

  updateSubjectColumn(index: number, header: string): void {
    const mapping = this.columnMapping();
    const cols = [...mapping.subjectColumns];
    cols[index] = header;
    this.columnMapping.set({ ...mapping, subjectColumns: cols });
  }

  updatePhoneColumn(index: number, header: string): void {
    const mapping = this.columnMapping();
    const cols = [...mapping.phoneColumns];
    cols[index] = header;
    this.columnMapping.set({ ...mapping, phoneColumns: cols });
  }

  removeSubjectColumn(index: number): void {
    const mapping = this.columnMapping();
    this.columnMapping.set({
      ...mapping,
      subjectColumns: mapping.subjectColumns.filter((_, i) => i !== index),
    });
  }

  removePhoneColumn(index: number): void {
    const mapping = this.columnMapping();
    this.columnMapping.set({
      ...mapping,
      phoneColumns: mapping.phoneColumns.filter((_, i) => i !== index),
    });
  }

  samplePreviewRow(): { subject: string; phone: string } | null {
    const parsed = this.parsedFile();
    if (!parsed) {
      return null;
    }
    const mapping = this.columnMapping();
    if (!mapping.subjectColumns.length || !mapping.phoneColumns.length) {
      return null;
    }
    const composed = composeMappedRows(parsed.headers, parsed.rows.slice(0, 1), mapping);
    return composed[0] ?? null;
  }

  canContinueFromUpload(): boolean {
    return Boolean(this.parsedFile());
  }

  canContinueFromMap(): boolean {
    const mapping = this.columnMapping();
    return mapping.subjectColumns.length > 0 && mapping.phoneColumns.length > 0;
  }

  buildPreviewRows(): void {
    const parsed = this.parsedFile();
    if (!parsed) {
      return;
    }
    const composed = composeMappedRows(parsed.headers, parsed.rows, this.columnMapping());
    const seen = new Map<string, number>();
    const rows: PreviewRow[] = composed.map((line, rowIndex) => {
      const phone = patientPhoneToE164(line.phone);
      let status: PreviewRow['status'] = 'ready';
      if (!line.subject.trim()) {
        status = 'missingSubject';
      } else if (!phone) {
        status = 'invalidPhone';
      } else {
        const key = `${line.subject.trim().toLowerCase()}|${phone}`;
        if (seen.has(key)) {
          status = 'duplicate';
        } else {
          seen.set(key, rowIndex);
        }
      }
      return {
        rowIndex,
        subject: line.subject.trim(),
        phone: phone ?? line.phone,
        status,
        skipped: status !== 'ready',
      };
    });
    this.previewRows.set(rows);
  }

  previewSummary(): { total: number; ready: number; attention: number } {
    const rows = this.previewRows();
    const ready = rows.filter((r) => !r.skipped && r.status === 'ready').length;
    return { total: rows.length, ready, attention: rows.length - ready };
  }

  canContinueFromPreview(): boolean {
    return this.previewRows().some((r) => !r.skipped && r.status === 'ready');
  }

  canContinueFromSettings(): boolean {
    if (!this.settingsForm.valid || !this.alertConfigForm.valid) {
      return false;
    }
    if (this.#filledCustomSlots().length === 0) {
      return false;
    }
    if (!this.settingsForm.controls.customScheduleEnabled.value) {
      return true;
    }
    return this.customSlots().controls.every((slot) => slot.valid);
  }

  campaignAlertConfig(): AlertConfig {
    return alertConfigFromForm(this.alertConfigForm, this.subjectLabels());
  }

  deliverySummary(): string {
    const channel = this.alertConfigForm.controls.notifyChannel.value as NotifyChannel;
    const labels: Record<NotifyChannel, string> = {
      both: 'Text and call',
      textOnly: 'Text only',
      callOnly: 'Call only',
    };
    return labels[channel];
  }

  scheduleSummary(): string {
    const filled = this.#filledCustomSlots().length;
    if (this.settingsForm.controls.customScheduleEnabled.value) {
      return `Custom · ${filled} attempt(s)`;
    }
    return `Calculated · ${filled} attempt(s)`;
  }

  toggleSkip(row: PreviewRow): void {
    this.previewRows.update((rows) =>
      rows.map((r) => (r.rowIndex === row.rowIndex ? { ...r, skipped: !r.skipped } : r)),
    );
  }

  customSlots(): FormArray {
    return this.settingsForm.controls.customSlots;
  }

  #ensureCycleSlotRows(): void {
    const slots = this.customSlots();
    while (slots.length < CAMPAIGN_CYCLE_UI_SLOTS) {
      slots.push(this.#createCycleSlotGroup());
    }
  }

  #createCycleSlotGroup(): FormGroup<{
    date: FormControl<string>;
    time: FormControl<string>;
  }> {
    return new FormGroup({
      date: new FormControl('', { nonNullable: true }),
      time: new FormControl('', { nonNullable: true }),
    });
  }

  #syncComputedCycleSlots(): void {
    const rows = cycleSlotsForCampaignUi(this.campaignAlertConfig(), Date.now());
    rows.forEach((row, index) => {
      this.customSlots()
        .at(index)
        .patchValue(row, { emitEvent: false });
    });
  }

  #applyCustomScheduleMode(custom: boolean): void {
    setAlertConfigRetryScheduleActive(this.alertConfigForm, !custom);
    if (!custom) {
      this.#syncComputedCycleSlots();
    }
    this.customSlots().controls.forEach((group, index) => {
      const slot = group as FormGroup<{ date: FormControl<string>; time: FormControl<string> }>;
      if (custom) {
        slot.setValidators([cycleSlotGroupValidator(index === 0)]);
        slot.enable({ emitEvent: false });
      } else {
        slot.clearValidators();
        slot.disable({ emitEvent: false });
      }
      slot.updateValueAndValidity({ emitEvent: false });
    });
    this.alertConfigForm.updateValueAndValidity({ emitEvent: false });
  }

  #filledCustomSlots(): { date: string; time: string }[] {
    return this.customSlots()
      .getRawValue()
      .filter((s) => s.date.trim() && s.time.trim());
  }

  goNext(): void {
    const current = this.step();
    if (current === 1) {
      this.buildPreviewRows();
    }
    if (current < STEP_TITLES.length - 1) {
      this.step.set(current + 1);
      this.#scheduleFormFieldReflow();
    }
  }

  goBack(): void {
    const current = this.step();
    if (current > 0) {
      this.step.set(current - 1);
      this.#scheduleFormFieldReflow();
    }
  }

  /** MDC labels in dialogs often mis-layout until focus; nudge after step change. */
  #scheduleFormFieldReflow(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  recordsToAlert(): number {
    return this.previewRows().filter((r) => !r.skipped && r.status === 'ready').length;
  }

  skippedCount(): number {
    return this.previewRows().filter((r) => r.skipped || r.status !== 'ready').length;
  }

  startCampaign(): void {
    if (!this.canContinueFromSettings() || this.recordsToAlert() === 0) {
      this.settingsForm.markAllAsTouched();
      this.alertConfigForm.markAllAsTouched();
      return;
    }
    const value = this.settingsForm.getRawValue();
    const roster: AlertCampaignRosterLine[] = this.previewRows().map((r) => ({
      subject: r.subject,
      phone: r.phone,
      skipped: r.skipped || r.status !== 'ready',
    }));

    const customSchedule = this.#filledCustomSlots();

    this.starting.set(true);
    this.#campaignService
      .startCampaign({
        title: value.title.trim(),
        description: value.description.trim() || undefined,
        locationId: value.locationId,
        alertConfig: this.campaignAlertConfig(),
        customSchedule,
        roster,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#dialogRef.close(true);
          void this.#router.navigate(['/contacts']).then(() => {
            this.#snackBar.open('Campaign started.', 'Okay', { duration: 4000 });
          });
        },
        error: () => {
          this.starting.set(false);
          this.#snackBar.open('Could not start campaign. Please try again.', 'Okay', {
            duration: 6000,
          });
        },
      });
  }
}
